import { useState, useEffect } from 'react';
import { Search, X, ArrowUpDown, Plus, Link } from 'lucide-react';
import type { Product } from '@/types/product';
import type { Vendor } from '@/types/vendor';
import { 
  getStoredProducts, 
  getStoredVendorAssociations, 
  addStoredVendorAssociation,
  subscribeToAssociationUpdates 
} from '../../../../utils/productStorage';

import {
  VendorAssociation
} from '@/types/vendor-association';

interface VendorProductsModalProps {
  vendor: Vendor;
  onClose: () => void;
}

interface SortConfig {
  key: keyof Product | 'lastPurchaseDate' | 'lastPurchasePrice';
  direction: 'asc' | 'desc';
}

export function VendorProductsModal({ vendor, onClose }: VendorProductsModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showLinkProduct, setShowLinkProduct] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'name',
    direction: 'asc',
  });
  const [associations, setAssociations] = useState(getStoredVendorAssociations());
  
  useEffect(() => {
    return subscribeToAssociationUpdates((updatedAssociations) => {
      setAssociations(updatedAssociations);
    });
  }, []);

  const allProducts = getStoredProducts();
  const vendorAssociations = associations.filter(a => a.vendorId === vendor.id);
  const associatedProductIds = new Set(vendorAssociations.map(a => a.productId));
  
  const products = allProducts.filter(product => {
    if (!showLinkProduct && !associatedProductIds.has(product.id)) {
      return false;
    }
    
    const matchesSearch = 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  const handleLinkProduct = (product: Product) => {
    if (!associatedProductIds.has(product.id)) {
      try {
        const association: VendorAssociation = {
          id: crypto.randomUUID(),
          vendorId: vendor.id,
          productId: product.id,
          preferredVendor: false,
          standardPrice: product.basePrice,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        addStoredVendorAssociation(association);
        setShowLinkProduct(false);
      } catch (error) {
        console.error('Error linking product:', error);
        // Could add error state and UI feedback here
      }
    }
  };

  const handleSort = (key: SortConfig['key']) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortedProducts = [...products].sort((a, b) => {
    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    
    if (sortConfig.key === 'lastPurchaseDate' || sortConfig.key === 'lastPurchasePrice') {
      // Handle custom sort for purchase history
      return 0; // Placeholder for purchase history implementation
    }
    
    const aValue = a[sortConfig.key as keyof Product];
    const bValue = b[sortConfig.key as keyof Product];
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return aValue.localeCompare(bValue) * direction;
    }
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return (aValue - bValue) * direction;
    }
    
    return 0;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-800">
            Products - {vendor.name}
          </h2>
          <div className="flex items-center space-x-4">
            <button
              type="button"
              onClick={() => setShowLinkProduct(!showLinkProduct)}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {showLinkProduct ? (
                <>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Link Product
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6 border-b">
          <div className="relative">
            <input
              type="text"
              placeholder="Search products..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
        </div>

        <div className="overflow-y-auto flex-1">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Product Name</span>
                    <ArrowUpDown className="h-4 w-4" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('sku')}
                >
                  <div className="flex items-center space-x-1">
                    <span>SKU</span>
                    <ArrowUpDown className="h-4 w-4" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('basePrice')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Price</span>
                    <ArrowUpDown className="h-4 w-4" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    <ArrowUpDown className="h-4 w-4" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Last Purchase
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedProducts.map((product) => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{product.name}</div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">{product.description}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{product.sku}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{formatCurrency(product.basePrice)}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                      ${product.status === 'active' ? 'bg-green-100 text-green-800' : 
                        product.status === 'discontinued' ? 'bg-red-100 text-red-800' : 
                        'bg-yellow-100 text-yellow-800'}`}>
                      {product.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div>Not available</div>
                  </td>
                  {showLinkProduct && !associatedProductIds.has(product.id) && (
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleLinkProduct(product)}
                        className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200"
                      >
                        <Link className="h-3 w-3 mr-1" />
                        Link
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}